import { useEffect } from 'react';
import { useAppSelector } from '@/hooks/redux';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import {
  getVapidPublicKey,
  getPushSubscriptions,
  registerPushSubscription,
} from '@/services/notificationService';
import { getDeviceName } from '@/utils/deviceName';

/**
 * Lightweight hook that runs at the app level to keep push subscriptions
 * in sync. If the browser has a push subscription that the server doesn't
 * know about (e.g., server auto-cleaned it after a 410), this re-registers it.
 *
 * Does NOT manage any UI state — that's left to usePushNotifications.
 */
export function usePushSubscriptionSync(): void {
  const { user } = useAppSelector((state) => state.auth);
  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    const sync = async () => {
      if (!isOnline || !user?.parentId) return;

      const supported =
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;
      if (!supported || Notification.permission !== 'granted') return;

      try {
        const vapidConfig = await getVapidPublicKey();
        if (!vapidConfig.enabled) return;

        const registrations = await navigator.serviceWorker.getRegistrations();
        if (registrations.length === 0) return;

        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        if (!existingSubscription) return;

        const serverSubscriptions = await getPushSubscriptions();
        const isOnServer = serverSubscriptions.some(
          (sub) => sub.endpoint === existingSubscription.endpoint
        );

        if (!isOnServer) {
          // Only auto-re-register if the user already has other subscriptions.
          // This prevents hijacking: if user A registers → logs out → user B logs in,
          // user B would have 0 subscriptions, so we skip auto-registration.
          if (serverSubscriptions.length === 0) {
            console.log('[PushSync] No existing subscriptions for this user — skipping auto-registration');
            return;
          }

          const subscriptionJson = existingSubscription.toJSON();
          if (subscriptionJson.endpoint && subscriptionJson.keys) {
            const deviceName = await getDeviceName();
            await registerPushSubscription(subscriptionJson, deviceName);
            console.log('[PushSync] Re-registered orphaned push subscription');
          }
        }
      } catch (err) {
        // Silent failure — this is a background sync, not user-facing
        console.error('[PushSync] Failed to sync push subscription:', err);
      }
    };

    sync();
  }, [isOnline, user?.parentId]);
}
